import { LitElement, html } from "lit-element";
import WebMidi from "webmidi";
import colorConvert, { rgb } from "color-convert";

import { scale, getRandomInRange, getRandomRGBColor } from "./utils";
const TERP = require("../scripts/TERP.js");

const CC_MAX = 127;

class RadialGradient extends LitElement {
    static get properties() {
        return {
            source: { type: String },
            orientation: { type: String },
            backgroundColor: { type: String },
            noteColor: { type: String },
            width: { type: String },
            height: { type: String },
            widthDivision: { type: Number },
            heightDivision: { type: Number },
            left: { type: String },
            top: { type: String },
            bottom: { type: String },
            right: { type: String },
            offset: { type: Number },
            position: { type: String },
            opacity: { type: Number },
            fill: { type: Boolean },
            lowNote: { type: Number },
            highNote: { type: Number },
            colorSystem: { type: String },
            colorValues: { type: Array },
            mixBlendMode: { type: String },
            ccResponsive: { type: Boolean },
            knobOneNumber: { type: Number },
            knobTwoNumber: { type: Number },
            knobThreeNumber: { type: Number },
            knobFourNumber: { type: Number },
            knobOneValue: { type: Number },
            knobTwoValue: { type: Number },
            knobThreeValue: { type: Number },
            knobFourValue: { type: Number },
            lightness: { type: Number },
            activeNotes: { type: Array },
            note: { type: Object },
            randomColor: { type: Boolean },

            debug: { type: Boolean },
            transitionDuration: { type: String },
        };
    }

    constructor() {
        super();

        this.source = "IAC Driver Bus 1";
        this.container = null;
        this.noteColor = "rgb(255, 255, 255)";
        this.orientation = "vertical";
        this.width = null;
        this.height = null;
        this.widthDivision = 1;
        this.heightDivision = 1;
        this.left = "0%";
        this.top = "0%";
        this.bottom = null;
        this.colorOptions = {};
        this.offset = 0;
        this.fill = false;
        this.lowNote = 21;
        this.highNote = 108;
        this.colorValues = [];
        this.mixBlendMode = "normal";
        this.position = "relative";
        this.knobOneNumber = null;
        this.knobTwoNumber = null;
        this.knobThreeNumber = null;
        this.knobFourNumber = null;
        this.knobOneValue = 0;
        this.knobTwoValue = 0;
        this.knobThreeValue = 0;
        this.knobFourValue = 0;
        this.ccResponsive = false;
        this.backgroundColor = "rgb(0, 0, 0)";
        this.lightness = 0;
        this.activeNotes = [];
        this.note = null;
        this.debug = false;
        this.randomColor = false;

        // radial gradient-specific properties

        WebMidi.enable((error) => {
            if (error) {
                console.log(error);
            } else {
                const arturia = WebMidi.getInputByName("Arturia MINILAB");
                const midiSource = WebMidi.getInputByName(this.source);
                midiSource.addListener(
                    "noteon",
                    "all",
                    this.handleNoteOn.bind(this)
                );
                // midiSource.addListener(
                //     "noteoff",
                //     "all",
                //     this.handleNoteOff.bind(this)
                // );

                midiSource.addListener(
                    "controlchange",
                    "all",
                    this.handleControlChange.bind(this)
                );
            }
        });
    }

    handleControlChange(e) {
        // console.log(`CC: ${e.controller.number} | val: ${e.value}`);
        if (e.controller.number === this.knobOneNumber) {
            this.knobOneValue = e.value > CC_MAX ? CC_MAX : e.value;
        }
        if (e.controller.number === this.knobTwoNumber) {
            this.knobTwoValue = e.value > CC_MAX ? CC_MAX : e.value;
        }
        if (e.controller.number === this.knobThreeNumber) {
            this.knobThreeValue = e.value > CC_MAX ? CC_MAX : e.value;
        }
        if (e.controller.number === this.knobFourNumber) {
            this.knobFourValue = e.value > CC_MAX ? CC_MAX : e.value;
        }
    }

    handleNoteOn(e) {
        if (e.note.number >= this.lowNote && e.note.number <= this.highNote) {
            this.container = this.shadowRoot.querySelector("#container");
            const element = document.createElement("div");
            element.id = `note${e.note.number}`;
            // write general style function
            const { note } = e;
            const scaledVelocity = TERP.scale(e.velocity, 0, 1, 0, 127);
            note.velocity = scaledVelocity;
            this.note = note;
            element.style = this.getNoteStyle(note);

            this.activeNotes.push(element);
            this.container.appendChild(element);
        }
    }

    // handleNoteOff(e) {
    //     if (e.note.number >= this.lowNote && e.note.number <= this.highNote) {
    //         const element = this.shadowRoot.querySelector(
    //             `#note${e.note.number}`
    //         );

    //         if (element) {
    //             //console.log('element removed');
    //             for (let i = 0; i < this.activeNotes.length; i++) {
    //                 if (this.activeNotes[i].id === element.id) {
    //                     this.activeNotes.splice(i, 1);
    //                     break;
    //                 }
    //             }
    //             this.container.removeChild(element);
    //         } else {
    //             console.log("no element to remove");
    //         }
    //     }
    // }

    getResponsiveColor(note) {
        console.log(`note velocity: ${note.velocity}`);
        const responsiveColor = this.colorValues.map((val) => {
            if (val === -1) {
                if (this.colorSystem === "cmyk") {
                    return Math.floor(scale(note.velocity, 0, 127, 30, 100));
                }
                if (this.colorSystem === "lab") {
                    return Math.floor(scale(note.velocity, 0, 127, 30, 100));
                }
                if (this.colorSystem === "rgb") {
                    return Math.floor(scale(note.velocity, 0, 127, 150, 255));
                }
            } else {
                return val;
            }
        });
        return responsiveColor;
    }

    getRGBColor(responsiveColor) {
        const system = this.colorSystem;
        let rgbColor = "";

        if (system === "rgb") {
            rgbColor = responsiveColor;
        }

        if (system === "cmyk") {
            rgbColor = colorConvert.cmyk.rgb(responsiveColor);
        }
        if (system === "lab") {
            rgbColor = colorConvert.lab.rgb(responsiveColor);
        }
        return rgbColor;
    }

    getNoteColor(note) {
        if (!note) {
            return;
        }
        let color = "";
        let rgb = "";
        const responsiveColor = this.getResponsiveColor(note);

        rgb = this.getRGBColor(responsiveColor);
        const a = scale(this.lightness, 0, 100, 0, 1);
        color = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${1})`;
        return color;
    }

    updateNoteColors() {
        // const noteSize = TERP.scale(this.note.velocity, 0, 127, 0, 100);
        const noteSize = 50;

        const containerHeight = window
            .getComputedStyle(this.container)
            .height.slice(0, -2);
        const containerWidth = window
            .getComputedStyle(this.container)
            .width.slice(0, -2);
        this.activeNotes.forEach((element) => {
            // const note = { number: element.id.slice(4, element.id.length) };
            element.style = `
      display: inline-block;
      position: absolute;
      width: ${containerWidth};
      height: ${containerHeight};
      background: radial-gradient(${
          this.noteColor ? this.noteColor : this.getNoteColor(this.note)
      } 2%, transparent ${noteSize}%);
      transition: opacity ${this.transitionDuration} ease-out;
      opacity: 0;
      `;
        });
    }

    getNoteStyle(note) {
        // const noteSize = TERP.scale(note.velocity, 0, 127, 0, 100);
        const noteSize = 100;
        let style = ``;
        const containerHeight = window
            .getComputedStyle(this.container)
            .height.slice(0, -2);
        const containerWidth = window
            .getComputedStyle(this.container)
            .width.slice(0, -2);

        style = `
    display: inline-block;
    position: absolute;
    width: ${containerWidth};
    height: ${containerHeight};
    background: radial-gradient(${
        this.noteColor ? this.noteColor : this.getNoteColor(note)
    } 2%, transparent ${noteSize}%);
    `;

        return style;
    }

    render() {
        if (this.container) {
            this.updateNoteColors();
        }

        if (WebMidi.supported) {
            return html`
                <style>
                    :host {
                      margin: 0 auto;
                    }
                    #container {
                      position: ${this.position};
                      display: inline-block;
                      width: ${this.width
                        ? this.width
                        : `calc(100% / ${this.widthDivision})`};

                      height: ${this.height
                        ? this.height
                        : `calc(100% / ${this.heightDivision})`};

                      left: ${this.left};
                      top: ${this.top};
                      background: ${this.ccResponsive
                        ? ccColor
                        : this.backgroundColor};
                      mixblendmode: ${this.mixBlendMode};
                      opacity: ${this.opacity};
                      outline: ${this.debug === true
                        ? "10px " + getRandomRGBColor()
                        : "none"};
                      outline-style: dashed;
                </style>
                <div id="container"></div>
            `;
        } else {
            return html``;
        }
    }
}

customElements.define("radial-gradient", RadialGradient);

// const r = TERP.scale(this.knobOneValue, 0, CC_MAX, 0, 255);
// const g = TERP.scale(this.knobTwoValue, 0, CC_MAX, 0, 255);
// const b = TERP.scale(this.knobThreeValue, 0, CC_MAX, 0, 255);
// this.lightness = colorConvert.rgb.lab([r, g, b])[0];
// const ccColor = `rgb(${r}, ${g}, ${b})`;
// this.updateNoteColors();
